import type { FetchResult } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { t } from "i18next";
import type { SetStateAction } from "react";

import {
  ACTIVATE_ROOT,
  ADD_ENVIRONMENT_INTEGRITY_EVENT,
  SYNC_GIT_ROOT,
} from "../../queries";
import type { IGitRootManagementDetailsAttr } from "../../types";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";
import { translate } from "utils/translations/translate";

interface IUseModifyGitRoot {
  activateRoot: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>;
  checkEvents: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>;
  syncGitRoot: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>;
  loading: boolean;
}

const useModifyGitRoot = (
  onUpdate: () => void,
  setCurrentRow: (
    value: SetStateAction<IGitRootManagementDetailsAttr | undefined>,
  ) => void,
): IUseModifyGitRoot => {
  const [activateRoot] = useMutation(ACTIVATE_ROOT, {
    onCompleted: (): void => {
      onUpdate();
      setCurrentRow(undefined);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        if (
          error.message ===
          "Exception - Root with the same URL/branch already exists"
        ) {
          msgError(translate.t("group.scope.common.errors.duplicateUrlBranch"));
        } else {
          msgError(translate.t("groupAlerts.errorTextsad"));
          Logger.error("Couldn't activate root", error);
        }
      });
    },
  });

  const [syncGitRoot, { loading }] = useMutation(SYNC_GIT_ROOT, {
    onCompleted: (): void => {
      onUpdate();
      msgSuccess(
        t("group.scope.git.sync.success"),
        t("group.scope.git.sync.successTitle"),
      );
      setCurrentRow(undefined);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        switch (error.message) {
          case "Exception - Access denied or credential not found":
            msgError(translate.t("group.scope.git.sync.noCredentials"));
            break;
          case "Exception - The root already has an active cloning process":
            msgError(translate.t("group.scope.git.sync.alreadyCloning"));
            break;
          case "Exception - Cloning job or task could not be queued":
            msgError(translate.t("group.scope.git.sync.couldNotBeQueued"));
            break;
          default:
            msgError(translate.t("groupAlerts.errorTextsad"));
            Logger.error("Couldn't queue root cloning", error);
        }
      });
    },
  });

  const [checkEvents] = useMutation(ADD_ENVIRONMENT_INTEGRITY_EVENT);

  return { activateRoot, checkEvents, loading, syncGitRoot };
};

export { useModifyGitRoot };
